// app/src/ReportSettings.jsx — report-scoped settings editors.
//
// These used to live as one long block on the Account screen. They've been
// split out so each report can surface its own matching rules behind a gear
// icon in its header (see GearButton in Widgets.jsx), keeping the setting where
// it's actually used. Each component is self-contained — it owns its state,
// loads on mount via the same window.kanjo.* APIs the Account screen used, and
// is meant to render as the BODY of a <Sheet> (it does not render its own
// Sheet). Persistence is unchanged: every list lives on the client userProfile
// Dexie row, already synced via backups + team snapshots.

// ─── Shared keyword editor (input + chip list + ×) ──────────────────────────
function DepositKeywordSection({ title, body, placeholder, empty, value, input, busy, onInput, onAdd, onRemove }) {
  return (
    <section>
      <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--ink-2,#666)', marginBottom: 8 }}>{title}</div>
      <p style={{ fontSize: 13, color: 'var(--ink-2,#666)', margin: '0 0 10px', lineHeight: 1.45 }}>{body}</p>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={input}
          onChange={(e) => onInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onAdd(); }}
          placeholder={placeholder}
          aria-label={title}
          style={{ flex: 1, padding: '12px 14px', fontSize: 15, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)', fontFamily: 'var(--mono)' }}
        />
        <button onClick={onAdd} disabled={busy || !input.trim()}
          style={{ padding: '0 18px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600,
            cursor: (busy || !input.trim()) ? 'default' : 'pointer',
            background: input.trim() ? 'var(--accent,#5b8def)' : 'var(--surface-3,#eee)',
            color: input.trim() ? '#fff' : 'var(--ink-3,#999)' }}>
          {t('account.add', 'Add')}
        </button>
      </div>
      {value.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          {value.map((k) => (
            <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 10px', borderRadius: 999, background: 'var(--surface-2,#f5f5f7)',
              fontSize: 13, fontFamily: 'var(--mono)', color: 'var(--ink,#111)' }}>
              {k}
              <button onClick={() => onRemove(k)} aria-label={`Remove ${k}`} disabled={busy}
                style={{ border: 'none', background: 'transparent', color: 'var(--ink-3,#999)', fontSize: 15, lineHeight: 1, cursor: 'pointer', padding: 0 }}>×</button>
            </span>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: 'var(--ink-3,#999)', marginTop: 10 }}>{empty}</div>
      )}
    </section>
  );
}

// Single-list keyword editor wiring (utilities, maintenance). Persists then
// reflects the saved list; backed by cashflow.expenseCategories.
function makeExpenseKwHandlers({ apiSet, value, setValue, input, setInput, setBusy }) {
  const save = async (next) => {
    setBusy(true);
    try { const saved = await apiSet(next); setValue(saved); }
    catch (e) { console.warn('[report-settings] expense kw save failed', e); }
    finally { setBusy(false); }
  };
  const add = () => {
    const v = input.trim();
    if (!v) return;
    setInput('');
    if (value.some((k) => k.toLowerCase() === v.toLowerCase())) return;
    save([...value, v]);
  };
  const remove = (k) => save(value.filter((x) => x !== k));
  return { add, remove };
}

// Deposit-keyword editor wiring, shared by the cash + card sections. Each save
// persists the list then re-derives sales so newly recognized deposits appear
// at once. `apiSet` is the channel-specific setter on the sales lib.
function makeKwHandlers({ apiSet, value, setValue, input, setInput, setBusy }) {
  const save = async (next) => {
    setBusy(true);
    try {
      const saved = await apiSet(next);
      setValue(saved);
      try { await window.kanjo.revenue.sales.syncFromDeposits(); } catch { /* non-fatal */ }
    } catch (e) { console.warn('[report-settings] deposit kw save failed', e); }
    finally { setBusy(false); }
  };
  const add = () => {
    const v = input.trim();
    if (!v) return;
    setInput('');
    if (value.some((k) => k.toLowerCase() === v.toLowerCase())) return;
    save([...value, v]);
  };
  const remove = (k) => save(value.filter((x) => x !== k));
  return { add, remove };
}

// ─── Owner's draw (Cash flow report) ────────────────────────────────────────
function OwnerDrawSettings() {
  useT();
  const [drawKw, setDrawKw] = React.useState([]);
  const [kwInput, setKwInput] = React.useState('');
  const [kwBusy, setKwBusy] = React.useState(false);
  const [drawStats, setDrawStats] = React.useState({}); // keyword → { count, totalCents }

  React.useEffect(() => {
    window.kanjo?.cashflow?.ownerDraw?.getKeywords?.().then((k) => setDrawKw(k || [])).catch(() => {});
  }, []);

  // Per-keyword match counts over the last 30 days of bank activity. Lets
  // the user see whether "1395" or "tariga" actually catches what they
  // think, instead of guessing about an abstract tag list.
  React.useEffect(() => {
    if (!drawKw.length) { setDrawStats({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const claims = await window.kanjo?.auth?.currentClaims?.().catch(() => null);
        if (!claims) return;
        const db = window.kanjo.db;
        const today = new Date();
        const cutoff = new Date(today.getTime() - 30 * 86400000).toISOString().slice(0, 10);
        const txs = await db.plaidTransactions
          .where('[userId+date]')
          .between([claims.sub, cutoff], [claims.sub, today.toISOString().slice(0, 10)], true, true)
          .toArray();
        const stats = {};
        for (const k of drawKw) {
          const needle = String(k).toLowerCase();
          let count = 0, totalCents = 0;
          for (const tx of txs) {
            if (tx.pending) continue;
            const hay = (tx.merchantName || '').toLowerCase() || (tx.description || '').toLowerCase();
            if (!hay.includes(needle)) continue;
            // Outflows only — keyword matches on inflows (rare contributions)
            // aren't draws; counting them would mislead the user about cost.
            if ((tx.amountCents || 0) <= 0) continue;
            count++;
            totalCents += tx.amountCents;
          }
          stats[k] = { count, totalCents };
        }
        if (!cancelled) setDrawStats(stats);
      } catch (e) { console.warn('[report-settings] draw stats failed', e); }
    })();
    return () => { cancelled = true; };
  }, [drawKw]);

  const saveKw = async (next) => {
    setKwBusy(true);
    try { const saved = await window.kanjo.cashflow.ownerDraw.setKeywords(next); setDrawKw(saved); }
    catch (e) { /* keep prior */ }
    finally { setKwBusy(false); }
  };
  const addKw = () => { const v = kwInput.trim().toLowerCase(); if (!v) return; setKwInput(''); if (!drawKw.includes(v)) saveKw([...drawKw, v]); };
  const removeKw = (v) => saveKw(drawKw.filter((k) => k !== v));

  // 401(k) / retirement labels — the operator's bank descriptor for their
  // retirement-plan transfer. Layered on top of the built-in provider hints so a
  // 401(k) outflow counts toward the owner's pocket (retirement), not an expense.
  const [k401Kw, setK401Kw] = React.useState([]);
  const [k401Input, setK401Input] = React.useState('');
  const [k401Busy, setK401Busy] = React.useState(false);
  React.useEffect(() => {
    window.kanjo?.cashflow?.ownerDraw?.get401kKeywords?.().then((k) => setK401Kw(k || [])).catch(() => {});
  }, []);
  const save401k = async (next) => {
    setK401Busy(true);
    try { const saved = await window.kanjo.cashflow.ownerDraw.set401kKeywords(next); setK401Kw(saved); }
    catch (e) { /* keep prior */ }
    finally { setK401Busy(false); }
  };
  const add401k = () => { const v = k401Input.trim().toLowerCase(); if (!v) return; setK401Input(''); if (!k401Kw.includes(v)) save401k([...k401Kw, v]); };
  const remove401k = (v) => save401k(k401Kw.filter((k) => k !== v));

  return (
    <section>
      <p style={{ fontSize: 13, color: 'var(--ink-2,#666)', margin: '0 0 10px', lineHeight: 1.45 }}>
        {t('account.ownerDrawBody')}
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={kwInput}
          onChange={(e) => setKwInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addKw(); }}
          placeholder={t('account.ownerDrawPlaceholder', 'e.g. transfer, 0000, distribution')}
          aria-label={t('account.ownerDraw', "Owner's draw")}
          style={{ flex: 1, padding: '12px 14px', fontSize: 15, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)' }}
        />
        <button onClick={addKw} disabled={kwBusy || !kwInput.trim()}
          style={{ padding: '0 18px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600, cursor: (kwBusy || !kwInput.trim()) ? 'default' : 'pointer', background: kwInput.trim() ? 'var(--accent,#5b8def)' : 'var(--surface-3,#eee)', color: kwInput.trim() ? '#fff' : 'var(--ink-3,#999)' }}>
          {t('account.add', 'Add')}
        </button>
      </div>
      {drawKw.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {drawKw.map((k) => {
            const s = drawStats[k];
            const matched = s && s.count > 0;
            return (
            <div key={k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 10, padding: '10px 12px', borderRadius: 10,
              background: 'var(--surface-2,#f5f5f7)',
              border: matched ? '1px solid transparent' : '1px dashed var(--rule)' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 14, fontFamily: 'var(--mono)', color: 'var(--ink,#111)' }}>{k}</div>
                <div style={{ fontSize: 11.5, color: matched ? 'var(--positive,#2e9e5b)' : 'var(--ink-3,#999)', marginTop: 2 }}>
                  {!s ? '…' : matched
                    ? <>matches {s.count} tx · <KMoney cents={s.totalCents} currency="USD" locale="en-US" style={{ fontSize: 11.5, color: 'var(--positive,#2e9e5b)' }}/> in last 30 days</>
                    : t('account.drawNoMatchHint', 'No bank activity matches this keyword in the last 30 days.')}
                </div>
              </div>
              <button onClick={() => removeKw(k)} aria-label={`Remove ${k}`} disabled={kwBusy}
                style={{ flexShrink: 0, border: 'none', background: 'transparent', color: 'var(--ink-3,#999)', fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: '4px 8px' }}>×</button>
            </div>
            );
          })}
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: 'var(--ink-3,#999)', marginTop: 10 }}>{t('account.noKeywords', 'No keywords yet — owner draws will count as expenses until you add some.')}</div>
      )}

      {/* 401(k) / retirement transaction labels — restored profile setting. */}
      <div style={{ borderTop: '1px solid var(--rule,#e5e2dc)', marginTop: 20, paddingTop: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink,#111)', marginBottom: 4 }}>
          {t('account.k401Title', '401(k) / retirement labels')}
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--ink-2,#666)', margin: '0 0 10px', lineHeight: 1.45 }}>
          {t('account.k401Body', "The descriptor your bank shows for your retirement-plan transfer. Matched transfers count toward your pocket as retirement, not a business expense. Common providers (Guideline, Fidelity, Voya, Empower…) are detected automatically — add yours here if it isn't.")}
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={k401Input}
            onChange={(e) => setK401Input(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add401k(); }}
            placeholder={t('account.k401Placeholder', 'e.g. guideline, ascensus, my401k')}
            aria-label={t('account.k401Title', '401(k) / retirement labels')}
            style={{ flex: 1, padding: '12px 14px', fontSize: 15, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)' }}
          />
          <button onClick={add401k} disabled={k401Busy || !k401Input.trim()}
            style={{ padding: '0 18px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600, cursor: (k401Busy || !k401Input.trim()) ? 'default' : 'pointer', background: k401Input.trim() ? 'var(--accent,#5b8def)' : 'var(--surface-3,#eee)', color: k401Input.trim() ? '#fff' : 'var(--ink-3,#999)' }}>
            {t('account.add', 'Add')}
          </button>
        </div>
        {k401Kw.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            {k401Kw.map((k) => (
              <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 999, background: 'var(--surface-2,#f5f5f7)', border: '1px solid var(--rule,#e5e2dc)' }}>
                <span style={{ fontSize: 13, fontFamily: 'var(--mono)', color: 'var(--ink,#111)' }}>{k}</span>
                <button onClick={() => remove401k(k)} aria-label={`Remove ${k}`} disabled={k401Busy}
                  style={{ border: 'none', background: 'transparent', color: 'var(--ink-3,#999)', fontSize: 16, lineHeight: 1, cursor: 'pointer', padding: 0 }}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Cash + card deposit keywords (Sales report) ────────────────────────────
function DepositKeywordSettings() {
  useT();
  const [cashKw, setCashKw] = React.useState([]);
  const [cashInput, setCashInput] = React.useState('');
  const [cashBusy, setCashBusy] = React.useState(false);
  const [cardKw, setCardKw] = React.useState([]);
  const [cardInput, setCardInput] = React.useState('');
  const [cardBusy, setCardBusy] = React.useState(false);

  React.useEffect(() => {
    window.kanjo?.revenue?.sales?.getCashDepositKeywords?.().then((k) => setCashKw(k || [])).catch(() => {});
    window.kanjo?.revenue?.sales?.getCardDepositKeywords?.().then((k) => setCardKw(k || [])).catch(() => {});
  }, []);

  const cash = makeKwHandlers({ apiSet: (n) => window.kanjo.revenue.sales.setCashDepositKeywords(n), value: cashKw, setValue: setCashKw, input: cashInput, setInput: setCashInput, setBusy: setCashBusy });
  const card = makeKwHandlers({ apiSet: (n) => window.kanjo.revenue.sales.setCardDepositKeywords(n), value: cardKw, setValue: setCardKw, input: cardInput, setInput: setCardInput, setBusy: setCardBusy });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
      {/* Cash-deposit keywords — every bank labels cash/check deposits
          differently (e.g. "DEPOSIT ID NUMBER …"), so the owner names theirs
          and they're counted as Cash sales. */}
      <DepositKeywordSection
        title={t('account.cashKeywords', 'Cash deposit keywords')}
        body={t('account.cashBody', 'Add the descriptor your bank puts on cash and check deposits (it varies by bank — e.g. "DEPOSIT ID NUMBER", "counter credit", "teller deposit"). Each match is counted as Cash sales and dated to the month it was earned.')}
        placeholder={t('account.cashPh', 'e.g. deposit id number')}
        empty={t('account.cashNone', "No keywords yet — cash and check deposits won't be counted until you add the descriptor your bank uses.")}
        value={cashKw} input={cashInput} busy={cashBusy}
        onInput={setCashInput} onAdd={cash.add} onRemove={cash.remove}
      />
      {/* Card-processor keywords — card settlements are recognized by processor
          name (Square, Toast, …), but the exact payout descriptor varies, so the
          owner can add their own. Each match is counted as Card sales. */}
      <DepositKeywordSection
        title={t('account.cardKeywords', 'Card processor keywords')}
        body={t('account.cardBody', "Add the descriptor your card processor's payouts show on your bank statement (it varies — e.g. \"SQUARE INC\", \"TST* TOAST\", \"STRIPE TRANSFER\"). Common processors are recognized automatically; add yours here if your card sales aren't showing up. Each match is counted as Card sales and settled to the month it was earned.")}
        placeholder={t('account.cardPh', 'e.g. square inc')}
        empty={t('account.cardNone', "No extra keywords — common processors (Square, Toast, Clover, Stripe…) are recognized automatically. Add a descriptor here if your card sales aren't being counted.")}
        value={cardKw} input={cardInput} busy={cardBusy}
        onInput={setCardInput} onAdd={card.add} onRemove={card.remove}
      />
    </div>
  );
}

// ─── Payroll check ranges (Payroll report) ──────────────────────────────────
function PayrollRangeSettings() {
  useT();
  const [payRanges, setPayRanges] = React.useState([]);
  const [payDraft, setPayDraft] = React.useState({ from: '', to: '', label: '' });
  const [payBusy, setPayBusy] = React.useState(false);

  React.useEffect(() => {
    window.kanjo?.cashflow?.payroll?.getCheckRanges?.().then((r) => setPayRanges(r || [])).catch(() => {});
  }, []);

  const savePayrollRanges = async (next) => {
    setPayBusy(true);
    try { const saved = await window.kanjo.cashflow.payroll.setCheckRanges(next); setPayRanges(saved); }
    catch (e) { console.warn('[report-settings] payroll ranges save failed', e); }
    finally { setPayBusy(false); }
  };
  const addPayrollRange = () => {
    const from = Number(payDraft.from) || 0;
    const to = Number(payDraft.to) || 0;
    if (from <= 0 || to < from) return;
    const next = [...payRanges, { from, to, label: payDraft.label.trim() }];
    setPayDraft({ from: '', to: '', label: '' });
    savePayrollRanges(next);
  };
  const removePayrollRange = (idx) => {
    const next = payRanges.filter((_, i) => i !== idx);
    savePayrollRanges(next);
  };

  return (
    <section>
      <p style={{ fontSize: 13, color: 'var(--ink-2,#666)', margin: '0 0 12px', lineHeight: 1.45 }}>
        {t('account.payrollRangesBody', 'Use a checkbook only for payroll, then enter its number range (e.g. 50000-50099). Any bank transaction matching the range counts as payroll in the Payroll report. Add one range per checkbook.')}
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <input value={payDraft.from}
          onChange={(e) => setPayDraft({ ...payDraft, from: e.target.value.replace(/[^\d]/g, '').slice(0, 8) })}
          inputMode="numeric" placeholder={t('account.payrollFrom', 'From')}
          aria-label={t('account.payrollFrom', 'From')}
          style={{ padding: '10px 12px', fontSize: 14, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)', fontFamily: 'var(--mono)' }}
        />
        <input value={payDraft.to}
          onChange={(e) => setPayDraft({ ...payDraft, to: e.target.value.replace(/[^\d]/g, '').slice(0, 8) })}
          inputMode="numeric" placeholder={t('account.payrollTo', 'To')}
          aria-label={t('account.payrollTo', 'To')}
          style={{ padding: '10px 12px', fontSize: 14, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)', fontFamily: 'var(--mono)' }}
        />
      </div>
      <input value={payDraft.label}
        onChange={(e) => setPayDraft({ ...payDraft, label: e.target.value.slice(0, 60) })}
        placeholder={t('account.payrollLabelExample', 'e.g. Wells Fargo payroll')}
        aria-label={t('account.payrollLabel', 'Label (optional)')}
        style={{ width: '100%', marginTop: 8, padding: '10px 12px', fontSize: 14, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)', boxSizing: 'border-box' }}
      />
      <button onClick={addPayrollRange}
        disabled={payBusy || !payDraft.from || !payDraft.to || Number(payDraft.to) < Number(payDraft.from)}
        style={{ width: '100%', marginTop: 10, padding: '12px', borderRadius: 10, border: 'none',
          fontSize: 14, fontWeight: 600,
          background: (payDraft.from && payDraft.to && Number(payDraft.to) >= Number(payDraft.from)) ? 'var(--accent,#5b8def)' : 'var(--surface-3,#eee)',
          color: (payDraft.from && payDraft.to) ? '#fff' : 'var(--ink-3,#999)',
          cursor: payBusy ? 'default' : 'pointer' }}>
        {t('account.payrollAdd', 'Add range')}
      </button>
      {payRanges.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {payRanges.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 10, padding: '10px 12px', borderRadius: 10, background: 'var(--surface-2,#f5f5f7)' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontFamily: 'var(--mono)', color: 'var(--ink,#111)' }}>
                  #{r.from} – #{r.to}
                </div>
                {r.label && (
                  <div style={{ fontSize: 12, color: 'var(--ink-3,#999)', marginTop: 2 }}>{r.label}</div>
                )}
              </div>
              <button onClick={() => removePayrollRange(i)} aria-label={`Remove range ${r.from}-${r.to}`} disabled={payBusy}
                style={{ border: 'none', background: 'transparent', color: 'var(--ink-3,#999)', fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: '4px 8px' }}>×</button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: 'var(--ink-3,#999)', marginTop: 10 }}>
          {t('account.payrollNone', "No ranges yet — payroll checks won't be detected until you add one.")}
        </div>
      )}
    </section>
  );
}

// ─── Tax keywords (Taxes report) ────────────────────────────────────────────
function TaxKeywordSettings() {
  useT();
  const [taxKw, setTaxKw] = React.useState({ excise: [], payroll: [], federal: [] });
  const [taxDraft, setTaxDraft] = React.useState({ excise: '', payroll: '', federal: '' });
  const [taxBusy, setTaxBusy] = React.useState(false);

  React.useEffect(() => {
    window.kanjo?.cashflow?.taxes?.getKeywords?.().then((k) => setTaxKw(k || { excise: [], payroll: [], federal: [] })).catch(() => {});
  }, []);

  const saveTaxKw = async (next) => {
    setTaxBusy(true);
    try { const saved = await window.kanjo.cashflow.taxes.setKeywords(next); setTaxKw((cur) => ({ ...cur, ...saved })); }
    catch (e) { console.warn('[report-settings] tax keywords save failed', e); }
    finally { setTaxBusy(false); }
  };
  const addTaxKw = (bucket) => {
    const v = String(taxDraft[bucket] || '').trim();
    if (!v) return;
    const cur = taxKw[bucket] || [];
    if (cur.some((k) => k.toLowerCase() === v.toLowerCase())) {
      setTaxDraft({ ...taxDraft, [bucket]: '' });
      return;
    }
    setTaxDraft({ ...taxDraft, [bucket]: '' });
    saveTaxKw({ [bucket]: [...cur, v] });
  };
  const removeTaxKw = (bucket, kw) => {
    const next = (taxKw[bucket] || []).filter((k) => k !== kw);
    saveTaxKw({ [bucket]: next });
  };

  return (
    <section>
      <p style={{ fontSize: 13, color: 'var(--ink-2,#666)', margin: '0 0 14px', lineHeight: 1.45 }}>
        {t('account.taxKeywordsBody', "Add the merchant names or descriptors that appear on your tax-payment transactions. Kanjo only counts what you tell it to — what shows up on your statement (e.g. \"ADP Tax\", \"WA DEPT REVENUE\", \"EFTPS\") is what to enter.")}
      </p>
      {[
        { id: 'excise',  label: t('account.taxBucketExcise',  'Excise / sales tax'),
          hint: t('account.taxBucketExciseHint',  'State/local sales or excise — e.g. WA DEPT REVENUE, CDTFA, state DOR.') },
        { id: 'payroll', label: t('account.taxBucketPayroll', 'Payroll tax'),
          hint: t('account.taxBucketPayrollHint', 'Federal/state payroll-tax remittance — e.g. ADP Tax, EFTPS, EDD.') },
        { id: 'federal', label: t('account.taxBucketFederal', 'Federal income tax'),
          hint: t('account.taxBucketFederalHint', 'Quarterly estimates and corporate filings — e.g. IRS USATAXPYMT, US TREASURY.') },
      ].map((bucket) => (
        <div key={bucket.id} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', marginBottom: 2 }}>{bucket.label}</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3,#999)', marginBottom: 8 }}>{bucket.hint}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={taxDraft[bucket.id]}
              onChange={(e) => setTaxDraft({ ...taxDraft, [bucket.id]: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') addTaxKw(bucket.id); }}
              placeholder={t('account.taxKeywordPh', 'e.g. ADP Tax')}
              aria-label={bucket.label}
              style={{ flex: 1, padding: '11px 13px', fontSize: 14, borderRadius: 10,
                border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)',
                color: 'var(--ink,#111)', fontFamily: 'var(--mono)' }}/>
            <button onClick={() => addTaxKw(bucket.id)}
              disabled={taxBusy || !String(taxDraft[bucket.id] || '').trim()}
              style={{ padding: '0 18px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600,
                background: String(taxDraft[bucket.id] || '').trim() ? 'var(--accent,#5b8def)' : 'var(--surface-3,#eee)',
                color: String(taxDraft[bucket.id] || '').trim() ? '#fff' : 'var(--ink-3,#999)',
                cursor: taxBusy ? 'default' : 'pointer' }}>
              {t('account.add', 'Add')}
            </button>
          </div>
          {(taxKw[bucket.id] || []).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              {(taxKw[bucket.id] || []).map((k) => (
                <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 10px', borderRadius: 999, background: 'var(--surface-2,#f5f5f7)',
                  fontSize: 12.5, fontFamily: 'var(--mono)', color: 'var(--ink,#111)' }}>
                  {k}
                  <button onClick={() => removeTaxKw(bucket.id, k)}
                    aria-label={`Remove ${k}`} disabled={taxBusy}
                    style={{ border: 'none', background: 'transparent', color: 'var(--ink-3,#999)',
                      fontSize: 14, lineHeight: 1, cursor: 'pointer', padding: 0 }}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
      <div style={{ fontSize: 11.5, color: 'var(--ink-3,#999)', lineHeight: 1.5, marginTop: 4 }}>
        {t('account.taxKeywordsFootnote', "Matching is case-insensitive and anchors to the start of a word, so a short identifier like \"941\" only hits when it stands alone — not when it appears mid-digit inside an ACH trace number.")}
      </div>
    </section>
  );
}

// ─── Landlord + utilities keywords (Rent & utilities report) ────────────────
function RentUtilitySettings() {
  useT();
  const [rentKw, setRentKw] = React.useState([]);
  const [rentInput, setRentInput] = React.useState('');
  const [rentBusy, setRentBusy] = React.useState(false);
  const [utilKw, setUtilKw] = React.useState([]);
  const [utilInput, setUtilInput] = React.useState('');
  const [utilBusy, setUtilBusy] = React.useState(false);

  React.useEffect(() => {
    window.kanjo?.cashflow?.rent?.getKeywords?.().then((k) => setRentKw(k || [])).catch(() => {});
    window.kanjo?.cashflow?.expenseCategories?.getUtilityKeywords?.().then((k) => setUtilKw(k || [])).catch(() => {});
  }, []);

  const saveRentKw = async (next) => {
    setRentBusy(true);
    try { const saved = await window.kanjo.cashflow.rent.setKeywords(next); setRentKw(saved); }
    catch (e) { console.warn('[report-settings] rent kw save failed', e); }
    finally { setRentBusy(false); }
  };
  const addRentKw = () => {
    const v = rentInput.trim();
    if (!v) return;
    setRentInput('');
    if (rentKw.some((k) => k.toLowerCase() === v.toLowerCase())) return;
    saveRentKw([...rentKw, v]);
  };
  const removeRentKw = (k) => saveRentKw(rentKw.filter((x) => x !== k));

  const util = makeExpenseKwHandlers({ apiSet: (n) => window.kanjo.cashflow.expenseCategories.setUtilityKeywords(n), value: utilKw, setValue: setUtilKw, input: utilInput, setInput: setUtilInput, setBusy: setUtilBusy });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
      {/* Landlord keywords — fuels the Rent & utilities report. Single
          bucket; the user enters the merchant/descriptor that lands on
          their rent + NNN transactions. */}
      <section>
        <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--ink-2,#666)', marginBottom: 8 }}>{t('account.landlordKeywords', 'Landlord keywords')}</div>
        <p style={{ fontSize: 13, color: 'var(--ink-2,#666)', margin: '0 0 10px', lineHeight: 1.45 }}>
          {t('account.landlordBody', 'Add the descriptors that appear on your rent and NNN payments — typically the landlord or property-management name. Each match becomes a rent line in the Rent & utilities report.')}
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={rentInput}
            onChange={(e) => setRentInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addRentKw(); }}
            placeholder={t('account.landlordPh', 'e.g. Panos Properties')}
            aria-label={t('account.landlordKeywords', 'Landlord keywords')}
            style={{ flex: 1, padding: '12px 14px', fontSize: 15, borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)', fontFamily: 'var(--mono)' }}
          />
          <button onClick={addRentKw} disabled={rentBusy || !rentInput.trim()}
            style={{ padding: '0 18px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600,
              cursor: (rentBusy || !rentInput.trim()) ? 'default' : 'pointer',
              background: rentInput.trim() ? 'var(--accent,#5b8def)' : 'var(--surface-3,#eee)',
              color: rentInput.trim() ? '#fff' : 'var(--ink-3,#999)' }}>
            {t('account.add', 'Add')}
          </button>
        </div>
        {rentKw.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            {rentKw.map((k) => (
              <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 10px', borderRadius: 999, background: 'var(--surface-2,#f5f5f7)',
                fontSize: 13, fontFamily: 'var(--mono)', color: 'var(--ink,#111)' }}>
                {k}
                <button onClick={() => removeRentKw(k)} aria-label={`Remove ${k}`} disabled={rentBusy}
                  style={{ border: 'none', background: 'transparent', color: 'var(--ink-3,#999)', fontSize: 15, lineHeight: 1, cursor: 'pointer', padding: 0 }}>×</button>
              </span>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: 'var(--ink-3,#999)', marginTop: 10 }}>
            {t('account.landlordNone', "No keywords yet — rent payments won't be detected until you add one.")}
          </div>
        )}
      </section>

      {/* Utilities keywords — electricity, gas, internet. Water/sewer/garbage
          are usually the landlord's, so they're left out by default. */}
      <DepositKeywordSection
        title={t('account.utilityKeywords', 'Utilities keywords')}
        body={t('account.utilityBody', 'Merchant names on your electricity, natural gas, and internet/phone bills (e.g. "Puget Sound Energy", "Comcast", "CenturyLink"). Matching charges are categorized as Utilities. Water, sewer, and garbage are usually the landlord\'s — leave those out.')}
        placeholder={t('account.utilityPh', 'e.g. comcast')}
        empty={t('account.utilityNone', 'No keywords — utility bills won\'t be auto-categorized until you add one.')}
        value={utilKw} input={utilInput} busy={utilBusy}
        onInput={setUtilInput} onAdd={util.add} onRemove={util.remove}
      />

      {/* MRO keywords — the report's third bucket (also editable from the
          Maintenance tab; same stored list). */}
      <MaintenanceKeywordSettings />
    </div>
  );
}

// ─── Maintenance keywords (Maintenance tab) ─────────────────────────────────
function MaintenanceKeywordSettings() {
  useT();
  const [maintKw, setMaintKw] = React.useState([]);
  const [maintInput, setMaintInput] = React.useState('');
  const [maintBusy, setMaintBusy] = React.useState(false);

  React.useEffect(() => {
    window.kanjo?.cashflow?.expenseCategories?.getMaintenanceKeywords?.().then((k) => setMaintKw(k || [])).catch(() => {});
  }, []);

  const maint = makeExpenseKwHandlers({ apiSet: (n) => window.kanjo.cashflow.expenseCategories.setMaintenanceKeywords(n), value: maintKw, setValue: setMaintKw, input: maintInput, setInput: setMaintInput, setBusy: setMaintBusy });

  return (
    <DepositKeywordSection
      title={t('account.maintKeywords', 'Maintenance keywords')}
      body={t('account.maintBody', 'Vendors for fire inspection, grease-trap and hood cleaning, dish-machine rental/maintenance, and repairs (e.g. "Cintas Fire", "Ecolab", "Auto-Chlor", a refrigeration or HVAC tech). Matching charges are categorized as Maintenance & repairs.')}
      placeholder={t('account.maintPh', 'e.g. ecolab')}
      empty={t('account.maintNone', 'No keywords — maintenance charges won\'t be auto-categorized until you add one.')}
      value={maintKw} input={maintInput} busy={maintBusy}
      onInput={setMaintInput} onAdd={maint.add} onRemove={maint.remove}
    />
  );
}

Object.assign(window, {
  DepositKeywordSection,
  OwnerDrawSettings,
  DepositKeywordSettings,
  PayrollRangeSettings,
  TaxKeywordSettings,
  RentUtilitySettings,
  MaintenanceKeywordSettings,
});
